<template>
  <v-container fluid>
    <page-title-area>
      <h1>{{ placeName }}</h1>
    </page-title-area>

    <v-card v-if="!loading && placeSensors.length === 0" outlined>
      <v-card-text>{{ $mpt($t('msg.no_sensors')) }}</v-card-text>
    </v-card>

    <template v-else>
      <v-card class="mb-5" outlined>
        <v-card-text class="d-flex align-center flex-wrap" style="gap: 16px">
          <div class="control-group">
            <span class="control-group__label">{{ $t('label.display_month') }}</span>
            <div class="d-inline-block compact-select" style="width: 90px;">
              <month-field
                :value="month"
                :max-date="currentMonth"
                dense
                outlined
                hide-details
                data-testid="month-selector"
                @input="month = $event"
              />
            </div>
          </div>

          <div class="control-group">
            <span class="control-group__label d-inline-flex align-baseline">
              {{ $mpt($t('label.sensor')) }}
              <v-tooltip v-if="hasRatSenseSensor" bottom max-width="300">
                <template #activator="{ on, attrs }">
                  <v-icon
                    x-small
                    class="ml-1"
                    :aria-label="ratSenseNotice"
                    data-testid="rat-sense-notice-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>{{ ratSenseNotice }}</span>
              </v-tooltip>
            </span>
            <v-select
              v-model="selectedSensorId"
              :items="sensorOptions"
              dense
              outlined
              hide-details
              class="small-select"
              style="max-width: 160px"
              data-testid="sensor-select"
            />
          </div>

          <div class="control-group">
            <span class="control-group__label">{{ $t('label.detection_type') }}</span>
            <v-btn-toggle v-model="filterMode" mandatory dense color="primary">
              <v-btn value="exclude_misdetection" small>{{ $t('filter.exclude_misdetection') }}</v-btn>
              <v-btn value="pickup_only" small>{{ $t('filter.pickup_only') }}</v-btn>
              <v-btn value="all" small>{{ $t('filter.all') }}</v-btn>
            </v-btn-toggle>
          </div>
          <v-spacer />
          <div class="control-group">
            <span class="control-group__label">&nbsp;</span>
            <v-btn
              icon
              small
              :loading="exporting"
              :disabled="exporting || loading"
              :aria-label="$t('action.download')"
              data-testid="download-detection-trend"
              @click="handleExportImage"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <div ref="exportContainer">
        <div v-show="exporting" class="detection-trend-export-info" data-testid="detection-trend-export-info">
          <div>{{ $t('export_info.facility') }}: {{ placeName }}</div>
          <div>{{ $mpt($t('label.sensor')) }}: {{ selectedSensorLabel }}</div>
          <div>{{ $t('export_info.period') }}: {{ month }}</div>
          <div>{{ $t('label.detection_type') }}: {{ filterModeLabel }}</div>
        </div>
        <v-card outlined>
          <!-- Custom Legend (捕獲記録がある場合のみ) -->
          <div
            v-if="featureIsActive('r_type_capture_record') && captureRecords.length > 0"
            class="chart-legend pa-4 pb-0"
          >
            <span class="legend-item">
              <span class="legend-line" :style="{ borderTopColor: primaryColor }"></span>
              {{ $t('legend.detection_count') }}
            </span>
            <span class="legend-item">
              <span
                class="legend-bar"
                :style="{ backgroundColor: captureRecordColor, borderColor: captureRecordBorderColor }"
              ></span>
              {{ $t('legend.capture_count') }}
            </span>
          </div>
          <v-card-text style="position: relative" data-testid="detection-trend-chart">
            <v-overlay :value="loading" absolute>
              <page-load-spinner />
            </v-overlay>
            <line-chart :data="chartData" :options="chartOptions" />
          </v-card-text>
        </v-card>
      </div>

      <!-- Capture Records Section -->
      <v-card v-if="featureIsActive('r_type_capture_record')" class="capture-records-section mt-6" outlined>
        <v-card-title class="d-flex align-center">
          <span class="capture-card-title">{{ $t('capture_records.title') }}</span>
          <v-spacer />
          <v-btn
            small
            color="primary"
            data-testid="add_capture_record_button"
            @click="openCaptureRecordEditor()"
          >
            <v-icon left small>add</v-icon>
            {{ $t('capture_records.add') }}
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-data-table
          :items="filteredCaptureRecords"
          :headers="captureRecordHeaders"
          :no-data-text="$t('capture_records.no_data')"
          hide-default-footer
          :items-per-page="captureRecordsPerPage"
          :page.sync="captureRecordPage"
          :sort-by="['date']"
          data-testid="capture_records_table"
        >
          <template #item.date="{ item }">
            {{ formatCaptureDate(item.date) }}
          </template>
          <template #item.species="{ item }">
            {{ speciesDisplayName(item.species) }}
          </template>
          <template #item.sensor_names="{ item }">
            <span v-if="!item.sensor_ids || item.sensor_ids.length === 0">-</span>
            <span
              v-for="id in (item.sensor_ids || [])"
              v-else
              :key="id"
              class="sensor-tag"
            >{{ sensorName(id) }}</span>
          </template>
          <template #item.memo="{ item }">
            <span
              class="memo-cell"
              :title="item.memo"
            >{{ item.memo }}</span>
          </template>
          <template #item.actions="{ item }">
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :data-testid="`capture_record_menu_${item.id}`"
                >
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  :data-testid="`edit_capture_record_${item.id}`"
                  @click="openCaptureRecordEditor(item)"
                >
                  <v-list-item-title>{{ $t('action.edit') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :data-testid="`delete_capture_record_${item.id}`"
                  @click="confirmDeleteCaptureRecord(item)"
                >
                  <v-list-item-title>{{ $t('action.delete') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
        <v-pagination
          v-if="captureRecordPageCount > 1"
          :length="captureRecordPageCount"
          v-model="captureRecordPage"
          color="grey"
          class="mt-2 ml-3"
          data-testid="capture_records_pagination"
        />
      </v-card>

      <!-- Capture Record Editor -->
      <capture-record-editor
        v-if="featureIsActive('r_type_capture_record')"
        v-model="captureRecordEditorVisible"
        :record="captureRecordEditTarget"
        :sensors="captureRecordEditorSensors"
        :place-id="placeId"
        @saved="onCaptureRecordSaved"
      />

      <!-- Delete Confirmation -->
      <confirm-dialog
        v-if="featureIsActive('r_type_capture_record')"
        v-model="captureRecordDeleteVisible"
        :message="$t('capture_records.delete_confirm')"
        @yes="deleteCaptureRecord"
      />
    </template>
  </v-container>
</template>

<i18n lang="yaml">
ja:
  label:
    display_month: 表示月
    sensor: センサー
    detection_type: 検出種別
    all_sensors: すべて
    date: 日付
    count: 検出数
    capture_count: 捕獲数
  filter:
    exclude_misdetection: 誤検出を除外
    pickup_only: ピックアップのみ
    all: すべて
  legend:
    detection_count: 検出数
    capture_count: 捕獲数
  tooltip:
    date_format: M/d
    unit: 件
  capture_records:
    title: 捕獲記録
    add: 捕獲記録を追加
    no_data: 捕獲記録がありません
    unknown_sensor: 不明なセンサー
    delete_confirm: この捕獲記録を削除しますか？
    header:
      date: 確認日
      species: 種類
      count: 捕獲数
      sensors: 最寄りのセンサー
      memo: メモ
  species:
    brown_rat: クマネズミ
    black_rat: ドブネズミ
    house_mouse: ハツカネズミ
    other: その他
    unknown: 不明
  action:
    edit: 編集
    delete: 削除
    download: 推移グラフを画像でダウンロード
  export_info:
    facility: 管理施設名
    period: 期間
  msg:
    no_sensors: この設置場所にセンサーが登録されていません
    load_error: データの読み込みに失敗しました
    fetch_error: イベントの取得に失敗しました
    detection_fetch_failed: 一部のセンサーの検出結果を取得できませんでした
    capture_records_fetch_error: 捕獲記録の取得に失敗しました
    delete_success: 捕獲記録を削除しました
    delete_error: 捕獲記録の削除に失敗しました
    export_error: エクスポートに失敗しました
    capture_record_added: '{year}年{month}月{day}日の捕獲記録を追加しました'
    capture_record_updated: '{year}年{month}月{day}日の捕獲記録を更新しました'
en:
  label:
    display_month: Month
    sensor: Sensor
    detection_type: Detection type
    all_sensors: All
    date: Date
    count: Detection count
    capture_count: Capture Count
  filter:
    exclude_misdetection: Exclude misdetections
    pickup_only: Pickup only
    all: All
  legend:
    detection_count: Detection Count
    capture_count: Capture Count
  tooltip:
    date_format: M/d
    unit: ""
  capture_records:
    title: Capture Records
    add: Add Capture Record
    no_data: No capture records
    unknown_sensor: Unknown sensor
    delete_confirm: "Delete this capture record?"
    header:
      date: Date
      species: Species
      count: Count
      sensors: Nearest Sensors
      memo: Memo
  species:
    brown_rat: Brown Rat
    black_rat: Black Rat
    house_mouse: House Mouse
    other: Other
    unknown: Unknown
  action:
    edit: Edit
    delete: Delete
    download: Download trend graph as image
  export_info:
    facility: Facility
    period: Period
  msg:
    no_sensors: No sensors are assigned to this facility
    load_error: Failed to load data
    fetch_error: Failed to fetch events
    detection_fetch_failed: Failed to fetch detections for some sensors
    capture_records_fetch_error: Failed to fetch capture records
    delete_success: Capture record deleted
    delete_error: Failed to delete capture record
    export_error: Export failed
    capture_record_added: 'Capture record added for {year}/{month}/{day}'
    capture_record_updated: 'Capture record for {year}/{month}/{day} updated'
</i18n>

<script>
import {
  startOfMonth, endOfMonth, eachDayOfInterval, startOfDay,
  addMonths, addDays, getUnixTime, format, parse, parseISO,
} from 'date-fns';
import { fetchAllPages } from '@/libs/fetchAllPages';
import AbortControl from '@/libs/AbortControl';
import theme from '@/libs/theme';
import {
  buildSensorSections, toSensorSelectItems, flattenSections, ratSenseDetectionNotice,
} from '@/libs/sensorSections';
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import SensorTypes, { SENSOR_TYPES } from '@/mixins/sensorTypes';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import PageLoadSpinner from '@/components/atoms/PageLoadSpinner';
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import MonthField from '@/components/molecules/MonthField';
import LineChart from '@/components/atoms/LineChart';
import { toBlob } from 'html-to-image';
import { saveAs } from 'file-saver';
import CaptureRecordEditor from './CaptureRecordEditor';

const CAPTURE_RECORD_COLOR = 'rgba(123, 31, 162, 0.55)';
const CAPTURE_RECORD_BORDER_COLOR = '#7b1fa2';

const CAPTURE_RECORDS_PER_PAGE = 10;

/** グラフ表示に必要な全イベントを少ないリクエスト回数で取得するための1回あたりの取得上限 */
const EVENTS_FETCH_LIMIT = 2000;

const INVALID_FILENAME_CHARS = /[/\\?%*:|"<>\s]/g;

async function exportImageFn(element, fileName) {
  const blob = await toBlob(element, {
    cacheBust: false,
    skipFonts: true,
    backgroundColor: '#ffffff',
  });
  if (!blob) {
    throw new Error('Failed to generate image blob');
  }
  saveAs(blob, fileName);
}

export default {
  components: {
    PageTitleArea,
    PageLoadSpinner,
    MonthField,
    LineChart,
    CaptureRecordEditor,
    ConfirmDialog,
  },

  mixins: [Features, Notifications, SensorTypes],

  inject: {
    api: 'api',
    exportImage: { default: () => exportImageFn },
  },

  props: {
    placeId: { type: String, required: true },
  },

  data() {
    return {
      month: format(new Date(), 'yyyy-MM'),
      selectedSensorId: null,
      filterMode: 'exclude_misdetection',
      loading: false,
      exporting: false,
      events: [],
      sensors: [],
      placeName: '',
      placeEntityIds: [],
      abortControl: new AbortControl(),
      captureRecords: [],
      captureRecordPage: 1,
      captureRecordEditorVisible: false,
      captureRecordEditTarget: null,
      captureRecordDeleteVisible: false,
      captureRecordDeleteTarget: null,
    };
  },

  computed: {
    currentMonth() {
      return format(new Date(), 'yyyy-MM');
    },

    primaryColor() {
      return theme.primaryColor;
    },

    captureRecordColor() {
      return CAPTURE_RECORD_COLOR;
    },

    captureRecordBorderColor() {
      return CAPTURE_RECORD_BORDER_COLOR;
    },

    sensorSections() {
      return buildSensorSections(this.sensors, {
        entityIds: this.placeEntityIds,
        typeNames: this.SENSOR_TYPE_NAMES,
      });
    },

    placeSensors() {
      return flattenSections(this.sensorSections);
    },

    sensorOptions() {
      return toSensorSelectItems(this.sensorSections, { allLabel: this.$t('label.all_sensors') });
    },

    hasRatSenseSensor() {
      return this.sensorSections.some(section => section.type === SENSOR_TYPES.RAT_SENSE);
    },

    ratSenseNotice() {
      return ratSenseDetectionNotice(this.$i18n.locale);
    },

    filteredEvents() {
      return this.events.filter(ev => {
        if (!ev.detected) return false;
        if (this.filterMode === 'exclude_misdetection' && ev.misdetection) return false;
        if (this.filterMode === 'pickup_only' && !ev.pickup) return false;
        if (this.selectedSensorId != null && ev.camera_id !== this.selectedSensorId) return false;
        return true;
      });
    },

    datesOfMonth() {
      const monthDate = parse(this.month, 'yyyy-MM', new Date());
      return eachDayOfInterval({
        start: startOfMonth(monthDate),
        end: endOfMonth(monthDate),
      });
    },

    dailyCounts() {
      return this.datesOfMonth.map(date => {
        const dayStart = getUnixTime(startOfDay(date));
        const dayEnd = getUnixTime(startOfDay(addDays(date, 1)));
        return this.filteredEvents.filter(
          ev => ev.timestamp >= dayStart && ev.timestamp < dayEnd,
        ).length;
      });
    },

    filteredCaptureRecords() {
      if (this.selectedSensorId == null) {
        return this.captureRecords;
      }
      return this.captureRecords.filter(
        r => r.sensor_ids && r.sensor_ids.includes(this.selectedSensorId),
      );
    },

    dailyCaptureCounts() {
      return this.datesOfMonth.map(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return this.filteredCaptureRecords
          .filter(r => r.date === dateStr)
          .reduce((sum, r) => sum + r.count, 0);
      });
    },

    hasCaptureData() {
      return this.dailyCaptureCounts.some(c => c > 0);
    },

    captureRecordsPerPage() {
      return CAPTURE_RECORDS_PER_PAGE;
    },

    captureRecordEditorSensors() {
      if (this.captureRecordEditTarget == null) return this.placeSensors;
      const placeSensorIds = new Set(this.placeSensors.map(s => s.id));
      const orphanedSensors = (this.captureRecordEditTarget.sensor_ids || [])
        .filter(id => !placeSensorIds.has(id))
        .map(id => this.sensors.find(s => s.id === id))
        .filter(s => s != null);
      return [...this.placeSensors, ...orphanedSensors];
    },

    captureRecordPageCount() {
      return Math.ceil(this.filteredCaptureRecords.length / CAPTURE_RECORDS_PER_PAGE);
    },

    captureRecordHeaders() {
      return [
        { text: this.$t('capture_records.header.date'), value: 'date', width: 100 },
        { text: this.$t('capture_records.header.species'), value: 'species', width: 130 },
        { text: this.$t('capture_records.header.count'), value: 'count', width: 100 },
        {
          text: this.$mpt(this.$t('capture_records.header.sensors')),
          value: 'sensor_names',
          sortable: false,
          cellClass: 'ellipsis-td',
          width: 180,
        },
        {
          text: this.$t('capture_records.header.memo'),
          value: 'memo',
          sortable: false,
          cellClass: 'ellipsis-td',
        },
        { text: '', value: 'actions', sortable: false, width: 48 },
      ];
    },

    chartData() {
      const datasets = [{
        label: this.$t('legend.detection_count'),
        data: this.dailyCounts,
        borderColor: theme.primaryColor,
        borderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 10,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: theme.primaryColor,
        tension: 0,
        fill: false,
        yAxisID: 'y',
      }];

      if (this.hasCaptureData) {
        datasets.push({
          label: this.$t('legend.capture_count'),
          type: 'bar',
          data: this.dailyCaptureCounts,
          backgroundColor: CAPTURE_RECORD_COLOR,
          borderColor: CAPTURE_RECORD_BORDER_COLOR,
          borderWidth: 1,
          yAxisID: 'y1',
        });
      }

      return {
        labels: this.datesOfMonth.map(d => format(d, 'd')),
        datasets,
      };
    },

    chartOptions() {
      const maxCount = Math.max(...this.dailyCounts, 1);

      const scales = {
        x: {
          title: { display: true, text: this.$t('label.date') },
        },
        y: {
          beginAtZero: true,
          suggestedMax: maxCount,
          ticks: {
            stepSize: this.tickStepSize(maxCount),
            precision: 0,
          },
          title: { display: true, text: this.$t('label.count') },
        },
      };

      if (this.hasCaptureData) {
        const maxCaptureCount = Math.max(...this.dailyCaptureCounts);
        scales.y1 = {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          suggestedMax: maxCaptureCount,
          ticks: {
            stepSize: this.tickStepSize(maxCaptureCount),
            precision: 0,
          },
          title: { display: true, text: this.$t('label.capture_count') },
          grid: { drawOnChartArea: false },
        };
      }

      return {
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: {
            position: 'nearest',
            callbacks: {
              title: (items) => format(this.datesOfMonth[items[0].dataIndex], 'M/d'),
              label: (item) => {
                const unit = this.$t('tooltip.unit');
                const suffix = unit === 'tooltip.unit' ? '' : unit;
                return `${item.dataset.label}: ${item.raw}${suffix}`;
              },
            },
          },
        },
        scales,
      };
    },

    selectedSensorLabel() {
      const option = this.sensorOptions.find(o => o.value === this.selectedSensorId);
      return option ? option.text : '';
    },

    filterModeLabel() {
      return this.$t(`filter.${this.filterMode}`);
    },
  },

  watch: {
    captureRecordPageCount(newCount) {
      if (this.captureRecordPage > newCount) {
        this.captureRecordPage = Math.max(1, newCount);
      }
    },

    month() {
      const signal = this.abortControl.newSignal();
      this.loading = true;
      const tasks = [this.fetchEvents(signal)];
      if (this.featureIsActive('r_type_capture_record')) {
        tasks.push(this.fetchCaptureRecords(signal));
      }
      Promise.all(tasks).finally(() => { this.loading = false; });
    },
  },

  async created() {
    this.loading = true;
    try {
      const [sensors, place] = await Promise.all([
        this.api.fetchSensors(),
        this.api.fetchRTypePlace(this.placeId),
      ]);
      this.sensors = sensors;
      this.placeName = place.name;
      this.placeEntityIds = place.entity_ids;
    } catch (error) {
      if (!AbortControl.isAbortError(error)) {
        this.notifyError('msg.load_error');
      }
      this.loading = false;
      return;
    }
    const signal = this.abortControl.newSignal();
    this.loading = true;
    try {
      const tasks = [this.fetchEvents(signal)];
      if (this.featureIsActive('r_type_capture_record')) {
        tasks.push(this.fetchCaptureRecords(signal));
      }
      await Promise.all(tasks);
    } finally {
      this.loading = false;
    }
  },

  beforeDestroy() {
    this.abortControl.abort();
  },

  methods: {
    tickStepSize(maxCount) {
      if (maxCount > 100) return 20;
      if (maxCount > 20) return 5;
      return 1;
    },

    async fetchEvents(signal) {
      const targetSensors = this.placeSensors;
      if (targetSensors.length === 0) {
        return;
      }

      try {
        const monthDate = parse(this.month, 'yyyy-MM', new Date());
        const results = await Promise.allSettled(
          targetSensors.map(sensor => this.fetchDetections(sensor, monthDate, signal)),
        );

        if (signal && signal.aborted) return;

        const { allEvents, failedSensors } = this.collectEvents(results, targetSensors);
        this.events = allEvents;

        // 取得失敗の通知は 1 度にまとめる。
        if (failedSensors.length > 0) {
          this.notifyError('msg.detection_fetch_failed');
        }
      } catch (error) {
        if (!AbortControl.isAbortError(error)) {
          this.notifyError('msg.fetch_error');
        }
      }
    },

    /** 表示月の期間を半開区間 [from, to) の unix 秒で返す。イベント取得 API はこの解釈で絞り込む */
    monthRangeHalfOpenUnix(monthDate) {
      return {
        from: getUnixTime(startOfMonth(monthDate)),
        to: getUnixTime(startOfMonth(addMonths(monthDate, 1))),
      };
    },

    /**
     * 表示月の期間を閉区間 [from, to] の unix 秒で返す。RatSense は from / to が指す日を両端とも含む。
     * 期間は端末のローカル時刻で組み立てるが、RatSense の検索条件はサーバー側で日本時間の日付へ丸められる。
     * 日本時間以外に設定された端末では日の境界がずれるため、利用する端末が日本時間であることを前提とする。
     */
    monthRangeClosedUnix(monthDate) {
      return {
        from: getUnixTime(startOfMonth(monthDate)),
        to: getUnixTime(endOfMonth(monthDate)),
      };
    },

    async fetchDetections(sensor, monthDate, signal) {
      if (sensor.type === SENSOR_TYPES.RAT_SENSE) {
        // RatSense は 1 回の応答で全件返るため、ページ送りの仕組みを通さない
        const period = this.monthRangeClosedUnix(monthDate);
        const response = await this.api.fetchRatSenseDetections(sensor.id, period, { signal });
        return response.events || [];
      }
      const params = { ...this.monthRangeHalfOpenUnix(monthDate), limit: EVENTS_FETCH_LIMIT };
      return this.fetchAllSensorEvents(sensor.id, params, signal);
    },

    async fetchCaptureRecords(signal) {
      const monthDate = parse(this.month, 'yyyy-MM', new Date());
      const from = format(startOfMonth(monthDate), 'yyyy-MM-dd');
      const to = format(endOfMonth(monthDate), 'yyyy-MM-dd');
      try {
        const records = await this.api.fetchRTypeCaptureRecords(
          this.placeId,
          { from, to },
        );
        if (signal && signal.aborted) return;
        this.captureRecords = records;
        this.captureRecordPage = 1;
      } catch (error) {
        if (!AbortControl.isAbortError(error)) {
          this.notifyError('msg.capture_records_fetch_error');
          this.captureRecords = [];
        }
      }
    },

    collectEvents(results, sensors) {
      const allEvents = [];
      const failedSensors = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          allEvents.push(...result.value);
        // 表示の切り替えで打ち切った取得は意図的な中止のため、失敗として数えない
        } else if (!AbortControl.isAbortError(result.reason)) {
          failedSensors.push(sensors[index]);
        }
      });
      return { allEvents, failedSensors };
    },

    async fetchAllSensorEvents(sensorId, params, signal) {
      return fetchAllPages(
        pageParams => this.api.fetchSensorEvents(sensorId, pageParams, { signal }),
        params,
        { signal },
      );
    },

    openCaptureRecordEditor(record = null) {
      this.captureRecordEditTarget = record;
      this.captureRecordEditorVisible = true;
    },

    onCaptureRecordSaved(record) {
      // saved emit は Editor の close() より前に発火するため、この時点で
      // captureRecordEditTarget は新規作成時のみ null、編集時は対象が入っている
      const isEditing = this.captureRecordEditTarget != null;
      const inViewMonth = record.date.slice(0, 7) === this.month;

      this.applyCaptureRecordToList(record, inViewMonth);

      const recordDate = parse(record.date, 'yyyy-MM-dd', new Date());
      this.notifySuccess(
        isEditing ? 'msg.capture_record_updated' : 'msg.capture_record_added',
        {
          year: format(recordDate, 'yyyy'),
          month: format(recordDate, 'M'),
          day: format(recordDate, 'd'),
        },
      );

      this.captureRecordEditorVisible = false;
      this.captureRecordEditTarget = null;
    },

    applyCaptureRecordToList(savedRecord, isInViewMonth) {
      const index = this.captureRecords.findIndex(r => r.id === savedRecord.id);
      if (isInViewMonth) {
        if (index >= 0) {
          this.$set(this.captureRecords, index, savedRecord);
        } else {
          this.captureRecords.push(savedRecord);
        }
      } else if (index >= 0) {
        this.captureRecords.splice(index, 1);
      }
    },

    confirmDeleteCaptureRecord(record) {
      this.captureRecordDeleteTarget = record;
      this.captureRecordDeleteVisible = true;
    },

    async deleteCaptureRecord() {
      try {
        await this.api.deleteRTypeCaptureRecord(this.captureRecordDeleteTarget.id);
        this.captureRecords = this.captureRecords.filter(
          r => r.id !== this.captureRecordDeleteTarget.id,
        );
        this.notifySuccess('msg.delete_success');
      } catch {
        this.notifyError('msg.delete_error');
      }
      this.captureRecordDeleteVisible = false;
    },

    speciesDisplayName(species) {
      return this.$t(`species.${species}`);
    },

    sensorName(id) {
      const sensor = this.sensors.find(s => s.id === id);
      return sensor != null ? sensor.name : this.$mpt(this.$t('capture_records.unknown_sensor'));
    },

    formatCaptureDate(dateStr) {
      return format(parseISO(dateStr), 'M/d');
    },

    async handleExportImage() {
      this.exporting = true;
      await this.$nextTick();
      try {
        await this.exportImage(this.$refs.exportContainer, this.buildExportFileName());
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.notifyError('msg.export_error');
      } finally {
        this.exporting = false;
      }
    },

    buildExportFileName() {
      const sanitize = str => str.replace(INVALID_FILENAME_CHARS, '_');
      return `${sanitize(this.placeName)}_${sanitize(this.selectedSensorLabel)}_${this.month}.png`;
    },
  },
};
</script>

<style scoped>
.control-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.control-group__label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}

.compact-select {
  font-size: 13px;
}

.compact-select ::v-deep .v-input__slot {
  min-height: 32px !important;
}

.compact-select ::v-deep .v-input__append-inner {
  margin-top: 5px !important;
}

.compact-select ::v-deep .v-select__selection,
.compact-select ::v-deep .v-text-field__slot input {
  font-size: 13px;
  text-align: center;
}

.small-select::v-deep .v-input__slot {
  min-height: 32px !important;
  height: 32px;
  font-size: 13px;
}

.small-select::v-deep .v-select__selections {
  font-size: 13px;
}

.small-select::v-deep .v-input__append-inner {
  margin-top: 5px !important;
}

.chart-legend {
  display: flex;
  gap: 16px;
  font-size: 13px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-line {
  display: inline-block;
  width: 24px;
  height: 0;
  border-top-style: solid;
  border-top-width: 2px;
}

.legend-bar {
  display: inline-block;
  width: 16px;
  height: 12px;
  border-style: solid;
  border-width: 1px;
}

.capture-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #757575;
}

.capture-records-section ::v-deep th {
  vertical-align: middle;
  line-height: 1.2;
  background: #fafafa;
}

.capture-records-section ::v-deep th.active {
  color: #757575;
}

.capture-records-section ::v-deep .ellipsis-td {
  max-width: 0;
}

.sensor-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e3f0fb;
  color: #4a72b8;
  font-size: 11px;
  margin: 0 3px 2px 0;
}

.memo-cell {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #757575;
  font-size: 13px;
}

.detection-trend-export-info {
  padding: 8px 16px;
  font-size: 12px;
  color: #333;
  line-height: 1.6;
}
</style>
